import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faAlignLeft, faClipboard, faSpellCheck, faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons';
import { P, match } from 'ts-pattern';

import { BedrockAgentType, TBedrockAgentType } from '@cloud-wave/neon-common-lib';
import { translate } from '@cloud-wave/neon-common-lib/app';

import { IQDisplayMode, TIQDisplayMode } from '../../constants/IQDisplayMode';

interface IIconProps {
  icon: IconDefinition;
}

interface IGetTitleProps {
  title: string;
  extendedTitle: string;
}

const getIconProps = ({ icon }: IIconProps) => ({
  [IQDisplayMode.COMPACT]: { iconType: icon, size: 18, color: 'black' },
  [IQDisplayMode.COMFORTABLE]: { iconType: icon, size: 17, color: 'black' },
  [IQDisplayMode.STAND_ALONE]: { iconType: icon, size: 13, color: 'aiPrimary' },
});

const getTitle = ({ title, extendedTitle }: IGetTitleProps) => {
  return {
    [IQDisplayMode.COMPACT]: extendedTitle,
    [IQDisplayMode.COMFORTABLE]: extendedTitle,
    [IQDisplayMode.STAND_ALONE]: title,
  };
};

export type TActionID = 'FEEDBACK' | 'SPELLING' | 'REWRITE' | 'SUMMARIZE';

export type TIQActionItem = {
  actionID: TActionID;
  title: Record<TIQDisplayMode, string>;
  iconProps: Record<TIQDisplayMode, { iconType: IconDefinition; size: number; color: string }>;
  childMenu: boolean;
};

type TIQActionItems = Record<string, TIQActionItem>;

export const IQActionItems: TIQActionItems = {
  evaluateEnabled: {
    actionID: 'FEEDBACK',
    title: getTitle({ title: 'Get feedback', extendedTitle: 'Give me feedback' }),
    iconProps: getIconProps({ icon: faClipboard }),
    childMenu: true,
  },
  [BedrockAgentType.AI_CORRECT]: {
    actionID: 'SPELLING',
    title: getTitle({ title: 'Fix spelling', extendedTitle: 'Fix spelling and grammar' }),
    iconProps: getIconProps({ icon: faSpellCheck }),
    childMenu: false,
  },
  [BedrockAgentType.AI_REFINE]: {
    actionID: 'REWRITE',
    title: getTitle({ title: 'Rewrite', extendedTitle: 'Rewrite this for me' }),
    iconProps: getIconProps({ icon: faWandMagicSparkles }),
    childMenu: true,
  },
  [BedrockAgentType.AI_EXTRACT]: {
    actionID: 'SUMMARIZE',
    title: getTitle({ title: `${translate('Summarise')}`, extendedTitle: `${translate('Summarise')} it` }),
    iconProps: getIconProps({ icon: faAlignLeft }),
    childMenu: false,
  },
};

export const getBedrockAgentForAction = (action: TActionID | null) =>
  match(action)
    .with('REWRITE', 'FEEDBACK', () => BedrockAgentType.AI_REFINE)
    .with('SPELLING', () => BedrockAgentType.AI_CORRECT)
    .with('SUMMARIZE', () => BedrockAgentType.AI_EXTRACT)
    .with(P.nullish, () => null)
    .otherwise(() => {
      throw new Error('No mapping from UI name to model name');
    }) as TBedrockAgentType | null;
